import { inject, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { map, Observable } from 'rxjs';
import { BaseApi } from './base/base-api';
import { ErrorCodeQuery } from '../../../shared/models/error-code/error-code-query';
import { Pagination } from '../../../shared/models/internal/pagination';
import { ErrorCode } from '../../../shared/models/error-code/error-code';
import { TranslationService } from '../translation.service';
import { CommonUtils, getPropertyName } from '../../common/common-utils';
import { CreateErrorCodeDto } from '../../../shared/models/error-code/create-error-code-dto';
import { ImportErrorCodesRequest as ImportErrorCodesRequest } from '../../../shared/models/error-code/import-error-codes-request';
import { optionalBoolToString } from './api-helpers/optional-bool-to-string';
import { ImportReport } from '../../../shared/models/report/import-report';
import { ImportTrDocDataRequest } from '../../../shared/models/error-code/import-tr-doc-data-request copy';

@Injectable({
  providedIn: 'root',
})
export class ErrorCodeApiService extends BaseApi {
  private translationService = inject(TranslationService);

  constructor() {
    super(environment.api + 'errorcode');
  }

  public getErrorCode(id: string, languageCode: string | null = null): Observable<ErrorCode> {
    if (languageCode == null) {
      languageCode = this.translationService.getLanguage();
    }

    return this.http
      .get<ErrorCode>(this.baseUrl + '/' + id + '?languageCode=' + languageCode)
      .pipe(map((ec) => this.mapErrorCode(ec, ec.languageCode ?? TranslationService.defaultLanguageCode())));
  }

  public getErrorCodes(
    query: ErrorCodeQuery,
    languageCodeTranslation: string | null = null,
    languageCodeTranslationsState: string | null = null
  ): Observable<Pagination<ErrorCode>> {
    if (languageCodeTranslation == null) {
      // lists are usually displayed in the default language.
      languageCodeTranslation = TranslationService.defaultLanguageCode();
    }
    if (languageCodeTranslationsState == null) {
      languageCodeTranslationsState = languageCodeTranslation;
    }

    const queryParams: Record<string, string> = {
      orderBy: query.orderBy ?? '',
      orderByDescending: query.orderByDescending ? 'true' : 'false',
      softDeleted: query.softDeleted ? 'true' : 'false',
      languageCode: languageCodeTranslation,
      translationLanguageCodeForSorting: languageCodeTranslationsState,
      searchInCode: optionalBoolToString(query.searchInCode, true),
      searchInMessage: optionalBoolToString(query.searchInMessage, true),
      needsReview: optionalBoolToString(query.needsReview, false),
      searchInDocumentationFields: optionalBoolToString(query.searchInDocumentationFields, false),
      searchInMachineTypes: optionalBoolToString(query.searchInMachineTypes, false),
      searchInOtherFields: optionalBoolToString(query.searchInOtherFields, false),
    };

    this.updateQueryParamsForReviewRelatedFilters(query, queryParams);
    this.updateQueryParamsForOther(query, queryParams);
    this.updateQueryParamsForRemedy(query, queryParams);
    this.updateQueryParamsForCauses(query, queryParams);
    this.updateQueryParamsForProjectAndNwNumbers(query, queryParams);

    return this.http
      .get<ErrorCode[]>(this.baseUrl + '?' + new URLSearchParams(queryParams).toString(), {
        observe: 'response',
      })
      .pipe(
        map((response) => {
          const errorCodes = response.body ?? [];
          const paginationHeader = response.headers.get(this.xPaginationHeader);

          if (paginationHeader) {
            const pagination = JSON.parse(paginationHeader) as Pagination<ErrorCode>;
            pagination.items = errorCodes.map((ec) => this.mapErrorCode(ec, languageCodeTranslationsState));
            return pagination;
          }
          throw new Error('Pagination header not found');
        })
      );
  }

  public importErrorCodes(dto: ImportErrorCodesRequest): Observable<ImportReport> {
    const formData = new FormData();
    if (dto.excelFile) {
      formData.append(getPropertyName<ImportErrorCodesRequest>('excelFile'), dto.excelFile, dto.excelFile.name);
    }
    if (dto.plainNwNumber) {
      formData.append(getPropertyName<ImportErrorCodesRequest>('plainNwNumber'), dto.plainNwNumber);
    }

    return this.http.post<ImportReport>(this.baseUrl + '/import', formData);
  }

  public importTrDocData(dto: ImportTrDocDataRequest, isXml: boolean): Observable<ImportReport> {
    const formData = new FormData();
    if (dto.file) {
      formData.append(getPropertyName<ImportTrDocDataRequest>('file'), dto.file, dto.file.name);
    }
    if (dto.plainNwNumber) {
      formData.append(getPropertyName<ImportErrorCodesRequest>('plainNwNumber'), dto.plainNwNumber);
    }

    return this.http.post<ImportReport>(
      this.baseUrl + '/import-tr-documentation-fields-' + (isXml ? 'xml' : 'xlsx'),
      formData
    );
  }

  public softDeleteErrorCode(id: string): Observable<void> {
    return this.http.put<void>(this.baseUrl + '/softdelete/' + id, null);
  }

  public deleteErrorCode(id: string): Observable<void> {
    return this.http.delete<void>(this.baseUrl + '/' + id);
  }

  public restoreErrorCode(id: string): Observable<void> {
    return this.http.put<void>(this.baseUrl + '/restore/' + id, null);
  }

  public updateErrorCode(errorCodeDto: ErrorCode): Observable<ErrorCode> {
    return this.http
      .put<ErrorCode>(this.baseUrl, errorCodeDto)
      .pipe(map((ec) => this.mapErrorCode(ec, ec.languageCode ?? TranslationService.defaultLanguageCode())));
  }

  public createErrorCode(createErrorCodeDto: CreateErrorCodeDto): Observable<ErrorCode> {
    return this.http.post<ErrorCode>(this.baseUrl, createErrorCodeDto).pipe(
      map((errorCode) => {
        return this.mapErrorCode(errorCode);
      })
    );
  }

  public getErrorCodeCategories(): Observable<string[]> {
    return this.http.get<string[]>(this.baseUrl + '/errorcodecategories');
  }

  public exportTrDocumentationFields(nwNumberId: string): Observable<Blob> {
    return this.http.get(this.baseUrl + '/export-tr-documentation-fields/' + nwNumberId, {
      responseType: 'blob',
    });
  }

  private mapErrorCode(ec: ErrorCode, languageCode?: string): ErrorCode {
    ec.displayId = ec.code ?? 'n/a';
    ec.extendedDisplayId = `Error Code ${ec.displayId}`;
    CommonUtils.decorateTranslationStateForLanguageCode(ec, languageCode);
    ec.nwNumbersString = (ec.nwNumbers ?? [])
      .map((nwn) => {
        return nwn.number ?? 'n/a';
      })
      .join(', ');
    return ec;
  }

  private updateQueryParamsForReviewRelatedFilters(query: ErrorCodeQuery, queryParams: Record<string, string>): void {
    if (query.isReviewedByAfterSales !== undefined) {
      queryParams[getPropertyName<ErrorCodeQuery>('isReviewedByAfterSales')] = query.isReviewedByAfterSales.toString();
    }

    if (query.hasActiveAfterSalesReview !== undefined) {
      queryParams[getPropertyName<ErrorCodeQuery>('hasActiveAfterSalesReview')] =
        query.hasActiveAfterSalesReview.toString();
    }

    if (query.isReviewedByTechnicalOrganisation !== undefined) {
      queryParams[getPropertyName<ErrorCodeQuery>('isReviewedByTechnicalOrganisation')] =
        query.isReviewedByTechnicalOrganisation.toString();
    }

    if (query.hasActiveTechnicalOrganisationReview !== undefined) {
      queryParams[getPropertyName<ErrorCodeQuery>('hasActiveTechnicalOrganisationReview')] =
        query.hasActiveTechnicalOrganisationReview.toString();
    }
  }

  private updateQueryParamsForOther(query: ErrorCodeQuery, queryParams: Record<string, string>): void {
    if (query.page) {
      queryParams[getPropertyName<ErrorCodeQuery>('page')] = query.page.toString();
    }

    if (query.pageSize) {
      queryParams[getPropertyName<ErrorCodeQuery>('pageSize')] = query.pageSize.toString();
    }

    if (query.searchPattern !== undefined) {
      queryParams[getPropertyName<ErrorCodeQuery>('searchPattern')] = query.searchPattern;
    }

    if (query.codes) {
      queryParams[getPropertyName<ErrorCodeQuery>('codes')] = query.codes;
    }

    if (query.nwNumber) {
      queryParams[getPropertyName<ErrorCodeQuery>('nwNumber')] = query.nwNumber;
    }

    if (query.translationStatesTerminal && query.translationStatesTerminal.length > 0) {
      queryParams[getPropertyName<ErrorCodeQuery>('translationStatesTerminal')] =
        query.translationStatesTerminal.join(',');
    }

    if (query.translationStatesDocumentation && query.translationStatesDocumentation.length > 0) {
      queryParams[getPropertyName<ErrorCodeQuery>('translationStatesDocumentation')] =
        query.translationStatesDocumentation.join(',');
    }

    if (query.mainLanguageDocumentationFieldsExportedForReview === true) {
      queryParams[getPropertyName<ErrorCodeQuery>('mainLanguageDocumentationFieldsExportedForReview')] =
        query.mainLanguageDocumentationFieldsExportedForReview.toString();
    }

    if (query.mainLanguageDocumentationFieldsReviewed !== undefined) {
      queryParams[getPropertyName<ErrorCodeQuery>('mainLanguageDocumentationFieldsReviewed')] =
        query.mainLanguageDocumentationFieldsReviewed.toString();
    }

    if (query.categories && query.categories.length > 0) {
      queryParams[getPropertyName<ErrorCodeQuery>('categories')] = query.categories.join(',');
    }

    if (query.changesBy) {
      queryParams[getPropertyName<ErrorCodeQuery>('changesBy')] = query.changesBy;
    }
  }

  private updateQueryParamsForRemedy(query: ErrorCodeQuery, queryParams: Record<string, string>): void {
    if (query.remedyInternNotEmpty !== undefined) {
      queryParams[getPropertyName<ErrorCodeQuery>('remedyInternNotEmpty')] = query.remedyInternNotEmpty.toString();
    }
    if (query.remedyDealerNotEmpty !== undefined) {
      queryParams[getPropertyName<ErrorCodeQuery>('remedyDealerNotEmpty')] = query.remedyDealerNotEmpty.toString();
    }
    if (query.remedyFarmerNotEmpty !== undefined) {
      queryParams[getPropertyName<ErrorCodeQuery>('remedyFarmerNotEmpty')] = query.remedyFarmerNotEmpty.toString();
    }
  }

  private updateQueryParamsForCauses(query: ErrorCodeQuery, queryParams: Record<string, string>): void {
    if (query.causeInternNotEmpty !== undefined) {
      queryParams[getPropertyName<ErrorCodeQuery>('causeInternNotEmpty')] = query.causeInternNotEmpty.toString();
    }
    if (query.causeDealerNotEmpty !== undefined) {
      queryParams[getPropertyName<ErrorCodeQuery>('causeDealerNotEmpty')] = query.causeDealerNotEmpty.toString();
    }
    if (query.causeFarmerNotEmpty !== undefined) {
      queryParams[getPropertyName<ErrorCodeQuery>('causeFarmerNotEmpty')] = query.causeFarmerNotEmpty.toString();
    }
  }

  private updateQueryParamsForProjectAndNwNumbers(query: ErrorCodeQuery, queryParams: Record<string, string>): void {
    if (query.projectId) {
      queryParams[getPropertyName<ErrorCodeQuery>('projectId')] = query.projectId.toString();
    }

    if (query.nwNumberIds && query.nwNumberIds.length > 0) {
      queryParams[getPropertyName<ErrorCodeQuery>('nwNumberIds')] = query.nwNumberIds.join(',');
    }
  }
}
